import React, { useRef, useState } from 'react';
import { Col, Tooltip } from 'syngenta-digital-cropwise-react-ui-kit';
import PropTypes from 'prop-types';

import formatRegistrationNumber from 'helpers/formatRegistrationNumber';
import './index.css';
import ProductTypeDisplay from 'screens/ProductList/helpers/ProductTypeDisplay';
import { capitalizeName } from 'helpers/textHelpers';
import {
  getManufacturerName,
  getProductName,
  getProductStandardUnit,
  getProductType,
  getRegistrationNumber
} from 'screens/ProductList/helpers/filteredProducts';

const ProductSearchListRow = ({
  product,
  primaryAction = null,
  secondaryAction = null,
  primaryIcon = null,
  secondaryIcon = null,
  isInUse = false
}) => {
  const nameRef = useRef(null);
  const manufacturerRef = useRef(null);
  const [showNameTooltip, setShowNameTooltip] = useState(false);
  const [showManufacturerTooltip, setShowManufacturerTooltip] = useState(false);

  const checkTruncation = (ref, setter) => {
    if (ref.current) {
      setter(ref.current.scrollWidth > ref.current.clientWidth);
    }
  };

  // Both actions add the product, so a row already on the list has to drop the
  // cell handlers too -- swapping the button for the checkmark alone still
  // leaves four columns that add it again.
  const rowAction = isInUse ? undefined : secondaryAction;
  const rowCursor = isInUse ? '' : ' cursor-pointer';

  return (
    <Col
      span={24}
      className="flex flex-row h-60px items-center hover:bg-info-light-blue"
      style={{ border: '0.5px solid #D8D8DA' }}
    >
      <Col className="flex flex-row justify-center items-center h-full w-120px">
        {isInUse ? (
          <img className="mr-1" src={secondaryIcon} alt="secondary icon" />
        ) : (
          <button
            type="button"
            onClick={primaryAction}
            className="add-product-btn"
            aria-label={`Add ${getProductName(product)}`}
          >
            <img src={primaryIcon} alt="" aria-hidden="true" />
          </button>
        )}
      </Col>
      <Col
        span={8}
        className={`flex flex-col justify-center h-full${rowCursor}`}
        onClick={rowAction}
      >
        <Tooltip
          title={showNameTooltip ? getProductName(product) : ''}
          color="#232630"
        >
          <p
            ref={nameRef}
            className="text-sm font-bold truncate"
            onMouseEnter={() => checkTruncation(nameRef, setShowNameTooltip)}
          >
            {getProductName(product)}
          </p>
        </Tooltip>
        <Tooltip
          title={showManufacturerTooltip ? getManufacturerName(product) : ''}
          color="#232630"
        >
          <p
            ref={manufacturerRef}
            className="text-neutral-60 text-xs truncate"
            onMouseEnter={() =>
              checkTruncation(manufacturerRef, setShowManufacturerTooltip)
            }
          >
            {getManufacturerName(product)}
          </p>
        </Tooltip>
      </Col>
      <Col
        span={4}
        className={`flex flex-row justify-start items-center h-full${rowCursor}`}
        onClick={rowAction}
      >
        <ProductTypeDisplay productType={getProductType(product)} />
      </Col>
      <Col
        span={3}
        className={`flex flex-col justify-center h-full${rowCursor}`}
        onClick={rowAction}
      >
        <p className="text-sm">
          {capitalizeName(getProductStandardUnit(product))}
        </p>
      </Col>
      <Col
        span={6}
        className={`flex flex-col justify-center h-full${rowCursor}`}
        onClick={rowAction}
      >
        <p className="text-sm">
          {formatRegistrationNumber(getRegistrationNumber(product)) || '---'}
        </p>
      </Col>
    </Col>
  );
};

ProductSearchListRow.propTypes = {
  product: PropTypes.shape({
    name: PropTypes.string,
    manufacturer: PropTypes.string,
    producttype: PropTypes.string,
    type: PropTypes.string,
    productType: PropTypes.string,
    stdunit: PropTypes.string,
    registrationnumber: PropTypes.string
  }).isRequired,
  primaryAction: PropTypes.func,
  secondaryAction: PropTypes.func,
  primaryIcon: PropTypes.string,
  secondaryIcon: PropTypes.string,
  isInUse: PropTypes.bool
};

export default ProductSearchListRow;
